import React, {
  createContext, useState, useMemo, useEffect, useCallback, useRef,
} from 'react';
import { useSearchParams, useNavigationType } from 'react-router-dom';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import {
  getCriteriaFromParams, buildSourceOptions, convertArrayToMultiselect, showError,
} from '../utils';
import useAxiosPrivate from '../hooks/useAxiosPrivate';

export const SearchContext = createContext({});

export const SearchProvider = ({ children }) => {
  const axiosPrivate = useAxiosPrivate();
  const [allSources, setAllSources] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const criteria = getCriteriaFromParams(searchParams);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState({});
  const navigationType = useNavigationType();

  const initialResultsRetrieved = useRef(false);

  const {
    body,
    from,
    ids,
    keywords,
    markedIrrelevant,
    sources,
    title,
    to,
  } = criteria;

  const articleSearch = useCallback(async (searchCriteria, { signal } = {}) => {
    try {
      setIsLoading(true);
      setSearchParams(searchCriteria);
      const { data = {} } = await axiosPrivate.post('/approved/search/', {
        signal,
        criteria: searchCriteria,
      });
      setResults(data);
    } catch (error) {
      showError({ error });
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, criteria]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      body,
      from,
      ids: convertArrayToMultiselect(ids),
      keywords: convertArrayToMultiselect(keywords),
      markedIrrelevant,
      sources: allSources?.length
        ? buildSourceOptions(sources, allSources)
        : convertArrayToMultiselect(sources),
      title,
      to,
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, actions) => {
      const newValues = {
        ...criteria,
        ...values,
        sources: values.sources.map((source) => Number(source.value)),
        ids: values.ids.map((id) => Number(id.value)),
        keywords: values.keywords.map((keyword) => keyword.value),
        page: 0,
        filteredSources: [],
      };
      articleSearch(newValues);
      actions.setSubmitting(false);
    },
  });

  const getSources = useCallback(async () => {
    const response = await axiosPrivate.get('/approved/getAllSources/');
    setAllSources(response.data);
  }, [axiosPrivate]);

  useEffect(() => {
    document.title = 'SCOTUSApp - Search';
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    try {
      getSources();
    } catch (error) {
      showError({ error });
    }

    return () => {
      controller.abort();
    };
  }, [getSources]);

  useEffect(() => {
    const controller = new AbortController();

    // check navigation type to trigger search on back / forward buttons
    if (!initialResultsRetrieved.current || navigationType === 'POP') {
      articleSearch(criteria, controller);
      initialResultsRetrieved.current = true;
    }

    return () => {
      controller.abort();
    };
  }, [articleSearch, criteria, navigationType]);

  const { handleReset } = formik;

  const resetSearch = useCallback(async () => {
    handleReset();
    const resetState = {
      body: '',
      filteredSources: [],
      from: '',
      ids: [],
      keywords: [],
      markedIrrelevant: false,
      page: 0,
      sources: [],
      title: '',
      to: '',
    };
    articleSearch(resetState);
  }, [articleSearch, handleReset]);

  const providerValue = useMemo(() => ({
    articleSearch,
    allSources,
    criteria,
    formik,
    isLoading,
    resetSearch,
    results,
  }), [formik, resetSearch]);

  return (
    <SearchContext.Provider value={providerValue}>
      {children}
    </SearchContext.Provider>
  );
};

SearchProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
